import {
  type MemoryState,
  isMemoryFull,
  memoryLabel,
  rememberedPhrase
} from '@/lib/memory';
import { useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle
} from '@/components/ui/sheet';

import { useIsMobile } from '@/hooks/use-mobile';

import MemoryBar from './MemoryBar';
import {
  useConversationMemory,
  useStartNewChat
} from './useConversationMemory';

/**
 * How many questions Signals remembers in this chat, beside the composer.
 *
 * Quiet on purpose: it asks for no attention until it is full. Hover or tap
 * explains it; on a phone a tap opens a sheet. Never tokens, percentages or
 * cache: only questions, the unit the user actually thinks in.
 */
export default function MemoryMeter() {
  const { state } = useConversationMemory();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number>();
  const startNewChat = useStartNewChat();

  if (!state || state.remembered <= 0) return null;

  const full = isMemoryFull(state);
  const label = memoryLabel(state);

  const pill = (
    <button
      type="button"
      id="memory-meter"
      aria-label={`${label}. What does this mean?`}
      onClick={() => setOpen((o) => !o)}
      className="flex items-center gap-2 rounded-full border px-2.5 min-h-[32px] text-[12px] leading-none whitespace-nowrap hover:bg-[var(--sig-panel)]"
      style={{
        borderColor: full
          ? 'var(--sig-warn-border)'
          : 'var(--sig-border-strong)',
        background: full ? 'var(--sig-warn-bg)' : undefined,
        color: full ? 'var(--sig-warn-text)' : 'var(--sig-body)'
      }}
    >
      <MemoryBar state={state} />
      {/* A phone composer has no room for the sentence; the sheet says it. */}
      <span>{isMobile ? `${state.remembered}/${state.max}` : label}</span>
    </button>
  );

  if (isMobile) {
    return (
      <>
        {pill}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent
            side="bottom"
            className="rounded-t-[20px] px-5 pt-6 pb-7 flex flex-col gap-3.5"
          >
            <SheetTitle className="text-[17px]">
              What Signals remembers
            </SheetTitle>
            <MemoryBar state={state} size="sheet" />
            <SheetDescription className="text-[14px] leading-relaxed text-[var(--sig-body)]">
              {full ? 'Its memory is full: ' : ''}
              Signals remembers {rememberedPhrase(state)}, so you can ask
              follow-ups like “and April?” without repeating yourself. It keeps
              up to {state.max}; older questions drop out.
            </SheetDescription>
            <p className="text-[14px] leading-relaxed text-[var(--sig-body)]">
              Your data is always complete: memory is about the conversation,
              not your numbers.
            </p>
            <div className="flex gap-2.5 pt-1">
              <Button
                variant="outline"
                className="flex-1 min-h-[46px]"
                onClick={() => setOpen(false)}
              >
                Got it
              </Button>
              <Button
                className="flex-1 min-h-[46px]"
                onClick={() => {
                  setOpen(false);
                  startNewChat();
                }}
              >
                New chat
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  // Hover opens it, leaving closes it after a beat so the pointer can travel
  // into the card to reach the link; a click pins it open like a tap.
  const hoverOpen = () => {
    window.clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const hoverClose = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), 200);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        asChild
        onMouseEnter={hoverOpen}
        onMouseLeave={hoverClose}
      >
        {pill}
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        className="w-[340px] flex flex-col gap-2 text-[13px] leading-relaxed"
        onMouseEnter={hoverOpen}
        onMouseLeave={hoverClose}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <MemoryCardText state={state} onNewChat={startNewChat} />
      </PopoverContent>
    </Popover>
  );
}

function MemoryCardText({
  state,
  onNewChat
}: {
  state: MemoryState;
  onNewChat: () => void;
}) {
  return (
    <>
      <div className="text-[14px] font-semibold text-[var(--sig-ink)]">
        Signals remembers {rememberedPhrase(state)}
      </div>
      <div className="text-[var(--sig-body)]">
        So follow-ups like “and April?” or “go back to the first one” just work.
        It keeps up to {state.max}; older questions drop out of its memory.
      </div>
      <div className="text-[var(--sig-body)]">
        Starting a different topic?{' '}
        <button
          type="button"
          className="underline underline-offset-2 text-primary hover:opacity-80"
          onClick={onNewChat}
        >
          Start a new chat
        </button>
      </div>
    </>
  );
}
